<script setup>
import { onMounted, ref, defineProps } from 'vue'
import dayjs from 'dayjs'
import staffTrainingService from '@/services/StaffTrainingService.js'
import { apiUrl } from '@/vars.js'

const courses = ref([])
const activeCourses = ref([])
const selectedCourse = ref(0)
const selectedDate = ref(dayjs().format('YYYY-MM-DD'))
const selectedFile = ref(null)

const props = defineProps({
  staff: { type: Object, required: true },
})

onMounted(async () => {
  try {
    // Load staff's existing trainings
    const data = await staffTrainingService.fetchTrainings(props.staff.id)
    activeCourses.value = data.map(training => ({
      id: training.id, // Use the training record ID
      courseId: training.course.id, // Related course ID
      name: training.course.name,
      attended: training.attended,
      evidence: training.file?.local || null,
      file_id: training.file?.id || null,
    }))


    // Fetch available courses
    courses.value = await staffTrainingService.fetchAvailableCourses()
    selectedCourse.value = courses.value.length ? courses.value[0].id : 0
  } catch (error) {
    console.error(error)
  }
})

const add = async () => {
  if (!selectedCourse.value || !selectedDate.value) {
    return alert('Course and completion date must be complete')
  }

  const course = courses.value.find(c => c.id === selectedCourse.value)
  console.log(selectedCourse.value)
  console.log(course)
  if (!course) {
    return alert('Invalid course selected')
  }

  try {
    const training = await staffTrainingService.addTraining(
      props.staff.id,
      selectedCourse.value,
      selectedDate.value,
      selectedFile.value || null
    )

    // Add to active courses
    const selectedCourseData = courses.value.find(course => course.id === selectedCourse.value);

    activeCourses.value.push({
      id: training.id, // Use the training record ID
      courseId: training.course_id,
      file_id: training.file_id,
      name: selectedCourseData.name, // Use the course name based on ID
      attended: training.attended,
      evidence: training.file?.local || null,
    });
  } catch (error) {
    console.error(error)
  }

  // Reset form
  selectedDate.value = dayjs().format('YYYY-MM-DD')
  selectedCourse.value = courses.value[0]?.id || 0
  selectedFile.value = null
}

const del = async course => {
  try {
    // Pass the training ID to the delete endpoint
    await staffTrainingService.deleteTraining(props.staff.id, course.id)
    activeCourses.value = activeCourses.value.filter(c => c.id !== course.id)
  } catch (error) {
    console.error(error)
  }
}
</script>

<template>
  <div class="space-y-5">
    <section class="rounded-2xl border border-slate-200 bg-slate-50/70 p-4 dark:border-slate-700 dark:bg-slate-900/30">
      <div class="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p class="text-xs uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">
            Add Training
          </p>
          <p class="text-sm text-slate-600 dark:text-slate-300">
            Record attendance and optionally upload evidence.
          </p>
        </div>
        <div class="rounded-full bg-white px-3 py-1 text-xs font-medium text-slate-600 dark:bg-slate-800 dark:text-slate-300">
          {{ activeCourses.length }} active record{{ activeCourses.length === 1 ? '' : 's' }}
        </div>
      </div>

      <div class="mt-4 grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1.45fr)_minmax(0,0.9fr)_minmax(0,1fr)_auto]">
        <div>
          <label class="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Course Name
          </label>
          <select class="w-full" v-model="selectedCourse">
            <option v-for="course in courses" :key="course.id" :value="course.id">
              {{ course.name }}
            </option>
          </select>
        </div>

        <div>
          <label class="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Attended Date
          </label>
          <input type="date" v-model="selectedDate" class="w-full" />
        </div>

        <div>
          <label class="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Evidence
          </label>
          <input type="file" class="w-full" @change="e => (selectedFile = e.target.files[0])" />
        </div>

        <div class="flex items-end">
          <button class="btn w-full xl:w-auto" @click.prevent="add">Add</button>
        </div>
      </div>
    </section>

    <section class="overflow-hidden rounded-2xl border border-slate-200 bg-white/80 dark:border-slate-700 dark:bg-slate-800/40">
      <div class="hidden grid-cols-[minmax(0,1.5fr)_minmax(0,0.9fr)_minmax(0,1fr)_auto] gap-4 border-b border-slate-200 bg-slate-50/80 px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:border-slate-700 dark:bg-slate-900/50 dark:text-slate-400 lg:grid">
        <span>Course Name</span>
        <span>Attended Date</span>
        <span>Evidence</span>
        <span class="text-right">Action</span>
      </div>

      <div v-if="activeCourses.length === 0" class="px-4 py-6 text-sm text-slate-500 dark:text-slate-400">
        No training records added yet.
      </div>

      <div v-else class="divide-y divide-slate-200 dark:divide-slate-700">
        <div
          v-for="course in activeCourses"
          :key="course.id"
          class="grid grid-cols-1 gap-3 px-4 py-4 lg:grid-cols-[minmax(0,1.5fr)_minmax(0,0.9fr)_minmax(0,1fr)_auto] lg:items-center lg:gap-4"
        >
          <div class="min-w-0">
            <div class="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400 lg:hidden">
              Course Name
            </div>
            <div class="truncate font-medium text-slate-900 dark:text-slate-100">
              {{ course.name }}
            </div>
          </div>

          <div>
            <div class="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400 lg:hidden">
              Attended Date
            </div>
            <div class="text-sm text-slate-700 dark:text-slate-200">
              {{ new Date(course.attended).toLocaleDateString('en-GB') }}
            </div>
          </div>

          <div>
            <div class="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400 lg:hidden">
              Evidence
            </div>
            <a
              v-if="course.file_id"
              :href="`${apiUrl}/files/${course.file_id}`"
              target="_blank"
              class="text-sm font-medium text-sky-600 hover:text-sky-700 dark:text-sky-300 dark:hover:text-sky-200"
            >
              View Evidence
            </a>
            <span v-else class="text-sm text-slate-500 dark:text-slate-400">No Evidence</span>
          </div>

          <div class="whitespace-nowrap lg:text-right">
            <DeleteButton :disableTitle="true" @delete="() => del(course)" />
          </div>
        </div>
      </div>
    </section>
  </div>
</template>
